import { FEEL, type ImpactTier } from '@/content/feel';

export interface Shake {
  x: number;
  y: number;
  roll: number;
}

function hash(n: number): number {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return (s - Math.floor(s)) * 2 - 1;
}

function fbm(t: number, seed: number): number {
  const a = hash(Math.floor(t) + seed);
  const b = hash(Math.floor(t) + 1 + seed);
  const f = t - Math.floor(t);
  const s = f * f * (3 - 2 * f);
  return a + (b - a) * s;
}

export function tierFor(severity: number, killed: boolean): ImpactTier {
  if (killed) return FEEL.kill;
  return severity >= FEEL.heavyFraction ? FEEL.heavy : FEEL.light;
}

export class Response {
  private trauma = 0;
  private clock = 0;
  private stopLeft = 0;
  private stopTotal = 0;
  private stopTier: ImpactTier = FEEL.light;
  private punchX = 0;
  private punchY = 0;
  private punchVX = 0;
  private punchVY = 0;
  private refractory = 0;
  readonly shake: Shake = { x: 0, y: 0, roll: 0 };

  hit(tier: ImpactTier, dirX: number, dirY: number): void {
    const span = tier.rampDown + tier.hold + tier.rampUp;
    if (this.refractory <= 0 && span > this.stopLeft) {
      this.refractory = FEEL.hitstopRefractory;
      this.stopLeft = Math.min(span, FEEL.hitstopBudget);
      this.stopTotal = this.stopLeft;
      this.stopTier = tier;
    }
    this.trauma = Math.min(1, Math.max(this.trauma, tier.trauma));
    const len = Math.hypot(dirX, dirY);
    if (len > 1e-6) {
      this.punchVX -= (dirX / len) * tier.punch * FEEL.punchOmega;
      this.punchVY -= (dirY / len) * tier.punch * FEEL.punchOmega;
    }
  }

  timeScale(): number {
    if (this.stopLeft <= 0) return 1;
    const t = this.stopTotal - this.stopLeft;
    const tier = this.stopTier;
    if (t < tier.rampDown) return 1 + (tier.scale - 1) * (t / Math.max(1e-6, tier.rampDown));
    if (t < tier.rampDown + tier.hold) return tier.scale;
    const k = (t - tier.rampDown - tier.hold) / Math.max(1e-6, tier.rampUp);
    return tier.scale + (1 - tier.scale) * Math.min(1, k);
  }

  frameUpdate(dt: number): void {
    this.clock += dt;
    if (this.refractory > 0) this.refractory = Math.max(0, this.refractory - dt);
    if (this.stopLeft > 0) this.stopLeft = Math.max(0, this.stopLeft - dt);

    this.trauma = Math.max(0, this.trauma - FEEL.traumaDecay * dt);
    const energy = Math.pow(this.trauma, FEEL.traumaExponent);
    const t = this.clock * FEEL.shakeHz;
    this.shake.x = fbm(t, 11) * FEEL.shakeAmplitude * energy;
    this.shake.y = fbm(t, 71) * FEEL.shakeAmplitude * energy;
    this.shake.roll = fbm(t, 131) * FEEL.shakeRoll * energy;

    const k = FEEL.punchOmega * FEEL.punchOmega;
    const c = 2 * FEEL.punchZeta * FEEL.punchOmega;
    this.punchVX += (-k * this.punchX - c * this.punchVX) * dt;
    this.punchVY += (-k * this.punchY - c * this.punchVY) * dt;
    this.punchX += this.punchVX * dt;
    this.punchY += this.punchVY * dt;
  }

  offsetX(): number {
    return this.shake.x + this.punchX;
  }

  offsetY(): number {
    return this.shake.y + this.punchY;
  }

  roll(): number {
    return this.shake.roll;
  }
}
